/**
 * Viewport and element size hooks for the layout rules in layout.ts.
 * window.innerWidth is the width CSS media queries test (the body never
 * scrolls, so there is no scrollbar to tell them apart), so a decision made
 * here and a Tailwind breakpoint at the same pixel value always agree.
 */
import { useLayoutEffect, useState, useSyncExternalStore } from 'react';

function subscribeResize(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
}

export function useViewportWidth(): number {
  return useSyncExternalStore(subscribeResize, () => window.innerWidth);
}

/**
 * A measurement of `el`, re-read by `measure` whenever a ResizeObserver sees
 * the element change size (zero while there is no element). Pass the element
 * from a callback ref (useState setter) so an element that mounts later is
 * still observed.
 */
export function useMeasure<T>(el: HTMLElement | null, measure: (el: HTMLElement) => T, initial: T, same: (a: T, b: T) => boolean): T {
  const [value, setValue] = useState(initial);
  useLayoutEffect(() => {
    if (!el) return;
    const update = () => {
      const next = measure(el);
      setValue((prev) => (same(prev, next) ? prev : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
    // `measure` and `same` are pure helpers; the element is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el]);
  return value;
}

const boxOf = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  return { width: r.width, height: r.height };
};
const sameBox = (a: { width: number; height: number }, b: { width: number; height: number }) =>
  a.width === b.width && a.height === b.height;

/** The element's border-box size. */
export function useElementSize(el: HTMLElement | null): { width: number; height: number } {
  return useMeasure(el, boxOf, { width: 0, height: 0 }, sameBox);
}
